import { Button, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PaywallCard } from '@/components/billing/HomeHQPaywall';
import { useAuthContext } from '@/context/AuthProvider';
import { getHomeHQPurchase } from '@/services/api/billing';

const HOME_HQ_PATH = '/dashboard/home-hub';

/**
 * Where a Stripe Payment Link sends the buyer after paying. They have no
 * Hauser account yet (or aren't logged in): point them at sign-up or log-in
 * with the purchase email, and the paywall links the purchase by that email.
 */
const PurchaseComplete = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const { data: purchase, isLoading } = useQuery({
    queryKey: ['homehq-purchase', sessionId],
    queryFn: () => getHomeHQPurchase(sessionId ?? ''),
    enabled: !!sessionId,
    retry: false,
    staleTime: Infinity,
  });

  if (isLoading) return null;

  const email = purchase?.email;
  const product = purchase?.planName ?? 'HomeHQ';

  return (
    <PaywallCard>
      <Stack spacing={1.5} sx={{ textAlign: 'center', width: '100%' }}>
        <Typography component="h1" sx={{ fontSize: 20, fontWeight: 600, textWrap: 'balance' }}>
          {`You're subscribed to ${product}`}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 14, textWrap: 'balance' }}>
          {user
            ? 'Your subscription is ready in Home HQ.'
            : email
              ? `Create your Hauser account with ${email} (or log in with it) and your subscription will be waiting.`
              : 'Create your Hauser account (or log in) with the email you used at checkout and your subscription will be waiting.'}
        </Typography>
      </Stack>

      <Stack spacing={1} sx={{ width: '100%' }}>
        {user ? (
          <Button fullWidth sx={{ fontSize: 14 }} onClick={() => navigate(HOME_HQ_PATH)}>
            Open Home HQ
          </Button>
        ) : (
          <>
            <Button
              fullWidth
              sx={{ fontSize: 14 }}
              onClick={() => navigate('/register', { state: { email } })}>
              Create my account
            </Button>
            <Button
              fullWidth
              variant="text"
              sx={{ fontSize: 13 }}
              onClick={() =>
                navigate('/login', { state: { email, from: { pathname: HOME_HQ_PATH } } })
              }>
              I already have an account
            </Button>
          </>
        )}
      </Stack>
    </PaywallCard>
  );
};

export default PurchaseComplete;
